import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import useUserQuery from '../hooks/queries/useUserQuery';
import { useBettieSettingInfo } from '../hooks/useBettieSettingInfo';
import BettiePersonaSuggestion from './components/BettiePersonaSuggestion';
import BettieLoading from './components/BettieLoading';
import BettieProfileImage from './components/BettieProfileImage';
import BettieIntroPreview from './components/BettieIntroPreview';
import BettieUserNickname from './components/BettieUserNickname';
import BettieSettingComplete from './components/BettieSettingComplete';
import StickyHeader from '../components/bar/StickyHeader';
import BettieMoodSuggestion from './components/BettieMoodSuggestion';
import type { components } from '../../types/schema';

export type BettieSettingStep =
  | 'room_intro'
  | 'persona_suggestion'
  | 'loading'
  | 'profile_image'
  | 'user_nickname'
  | 'mood_suggestion'
  | 'complete';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

const BettieSettingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<BettieSettingStep>('room_intro');
  const [bettiePersona, setBettiePersona] = useState<ApiV1MemberPersona | null>(null);
  const [chatRoomUuid, setChatRoomUuid] = useState<string>('');
  const { bettieSettingInfo, updateBettieSettingInfo, resetBettieSettingInfo } =
    useBettieSettingInfo();
  const [stepHistory, setStepHistory] = useState<BettieSettingStep[]>([]);

  const { colorMode } = useColorMode();

  // Allow external navigation to set initial step and persona
  useEffect(() => {
    const state = location.state as
      | { initialStep?: BettieSettingStep; bettiePersona?: ApiV1MemberPersona }
      | undefined;
    if (state?.initialStep) {
      setCurrentStep(state.initialStep);
    }
    if (state?.bettiePersona) {
      setBettiePersona(state.bettiePersona);
    }
  }, [location.state]);

  useEffect(() => {
    return () => resetBettieSettingInfo();
  }, [resetBettieSettingInfo]);

  const goToPreviousStep = (nextStep: BettieSettingStep) => {
    setStepHistory((prev) => [...prev, currentStep]);
    setCurrentStep(nextStep);
  };

  const handleBackClick = () => {
    if (currentStep === 'profile_image') {
      return;
    }

    if (currentStep === 'complete') {
      navigate('/bettie-chat-list', { replace: true });
      return;
    }
    if (stepHistory.length > 0) {
      const prevStep = stepHistory[stepHistory.length - 1];
      setStepHistory((prev) => prev.slice(0, -1));
      setCurrentStep(prevStep);
      return;
    }
    navigate(-1);
  };

  const { data: user } = useUserQuery();

  return (
    <div
      className={`min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)] ${currentStep === 'profile_image' ? 'bg-pure-black' : 'bg-background-light dark:bg-background-dark'}`}
    >
      <Helmet>
        <title>Bettie</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#222222' : '#FFFFFF'} />
      </Helmet>
      {currentStep !== 'profile_image' && currentStep !== 'mood_suggestion' && (
        <StickyHeader
          showBackButton={true}
          showTitle={false}
          alwaysShowTitle={false}
          onClickBackButton={handleBackClick}
        />
      )}
      {currentStep === 'room_intro' && (
        <BettieIntroPreview setCurrentStep={goToPreviousStep} setBettiePersona={setBettiePersona} />
      )}
      {currentStep === 'persona_suggestion' && (
        <BettiePersonaSuggestion
          bettieSettingInfo={bettieSettingInfo}
          updateBettieSettingInfo={updateBettieSettingInfo}
          setCurrentStep={goToPreviousStep}
          setBettiePersona={setBettiePersona}
        />
      )}
      <div className="relative">
        <AnimatePresence>
          {currentStep === 'loading' && (
            <motion.div
              key="loading"
              className="absolute inset-0"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <BettieLoading
                bettieName={bettieSettingInfo.bettieNickname}
                bettiePersona={bettiePersona as ApiV1MemberPersona}
                setCurrentStep={goToPreviousStep}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {currentStep === 'profile_image' && (
            <motion.div
              key="profile_image"
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <BettieProfileImage
                bettieSettingInfo={bettieSettingInfo}
                updateBettieSettingInfo={updateBettieSettingInfo}
                setCurrentStep={goToPreviousStep}
                setStepHistory={setStepHistory}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {currentStep === 'user_nickname' && (
        <BettieUserNickname
          bettiePersona={bettiePersona as ApiV1MemberPersona}
          bettieSettingInfo={bettieSettingInfo}
          updateBettieSettingInfo={updateBettieSettingInfo}
          defaultUserNickname={user?.baseUserProfile?.nickname ?? ''}
          setChatRoomUuid={setChatRoomUuid}
          setCurrentStep={goToPreviousStep}
        />
      )}
      {currentStep === 'mood_suggestion' && (
        <BettieMoodSuggestion
          setCurrentStep={setCurrentStep}
          bettiePersona={bettiePersona as ApiV1MemberPersona}
          chatRoomUuid={chatRoomUuid}
        />
      )}
      {currentStep === 'complete' && (
        <BettieSettingComplete
          bettiePersona={bettiePersona as ApiV1MemberPersona}
          bettieSettingInfo={bettieSettingInfo}
          chatRoomUuid={chatRoomUuid}
        />
      )}
    </div>
  );
};

export default BettieSettingPage;
